import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  Button,
  Dimmer,
  Form,
  Loader,
  Table,
} from 'semantic-ui-react';
import moment from 'moment-timezone';
import qs from 'qs';
import { required } from '@mhub/web-validations';
import { flashErrorMessage } from '../actions/flash';
import { fetchM } from '../actions/fetchM';
import { namespaces, TRANSLATION_FILES } from '../constants/translation';
import AppForm from './AppForm';

class FinanceSourceList extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    loading: PropTypes.bool,
    onOriChange: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    isForeignCompany: PropTypes.bool,
    t: PropTypes.func,
  }
  constructor(props) {
    super(props);
    this.state = {
      count: 0,
      loading: props.loading || true,
      data: [],
      errMessages: [],
    };
    this.validation = {};
  }
  componentDidMount() {
    this.fetchFinanceSources();
  }
  setComponentState(state = {}) {
    return new Promise((res) => {
      this.setState(state, () => {
        res(state);
      });
    });
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  async validate() {
    const { data } = this.state;
    const stateData = data.reduce((d, cur) => ({
      ...d,
      [`${cur.id}_label`]: cur.label,
    }), {});
    return AppForm.validate(this.validation, stateData)
      .then((validations) => {
        this.setState({
          errMessages: validations.filter(v => !v.isValid),
        });
        return validations;
      });
  }
  async fetchFinanceSources() {
    await this.setLoading(true);
    const { t } = this.props;
    const { store } = this.context;
    const query = {
      page: 'all',
    };
    let res;
    try {
      res = await store.dispatch(fetchM(`/finance_sources/?${qs.stringify(query)}`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: t('An error occurred, please refresh page', { ns: TRANSLATION_FILES.translation }) }));
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const count = parseInt(res.headers.get('M-Data-count'), 10);
      const token = res.headers.get('X-Csrf-Token');
      const data = await res.json();
      // handle token.. etc
      await this.setComponentState({
        count,
        token,
        data,
      });
      this.props.onOriChange(data);
    }
    await this.setLoading(false);
  }
  handleLabelChange = (event, { name, value }) => {
    const { data } = this.state;
    const index = data.findIndex(d => d.id === name);
    if (index >= 0) {
      data[index].label = value;
    }
    this.setState({ data }, () => {
      this.props.onChange(data);
    });
  }
  handleEnabledChange = (event, { name }) => {
    const { data } = this.state;
    const index = data.findIndex(f => f.id === name);
    if (index >= 0) {
      data[index].isEnabled = !data[index].isEnabled;
    }
    this.setState({ data }, () => {
      this.props.onChange(data);
    });
  }
  handleOnAddFinanceSource = () => {
    const newData = this.state.data;
    newData.push({
      id: moment().toDate().getTime(),
      label: '',
      isDefault: false,
      isEnabled: false,
    });
    this.setState({ data: newData }, () => {
      this.props.onChange(newData);
    });
  }
  handleOnRemoveFinanceSource = (id) => {
    const newData = this.state.data.filter(d => d.id !== id);
    this.setState({ data: newData }, () => {
      this.props.onChange(newData);
    });
  }

  renderFinanceItem = (key, field) => (
    <Table.Row key={key}>
      <Table.Cell width={9}>
        {
          field.isDefault ? this.props.t(field.label, { ns: TRANSLATION_FILES.constants }) : (
            <AppForm.Input
              validateLabel="Label"
              name={field.id}
              value={field.label}
              validation={[required]}
              onChange={this.handleLabelChange}
              ref={(ref) => { this.validation[`${field.id}_label`] = ref; }}
            />
          )
        }
      </Table.Cell>
      <Table.Cell width={3} textAlign="right">
        <Form.Checkbox
          toggle
          checked={field.isEnabled}
          name={field.id}
          onChange={this.handleEnabledChange}
          disabled={!this.props.isForeignCompany && field.id.toString().includes('referred_banker')}
        />
      </Table.Cell>
      <Table.Cell width={1} textAlign="right">
        {
          !field.createdAt && (
            <Button
              floated="right"
              negative
              type="button"
              size="small"
              icon="trash"
              onClick={() => this.handleOnRemoveFinanceSource(field.id)}
            />
          )
        }
      </Table.Cell>
    </Table.Row>
  );

  render() {
    const { t } = this.props;
    return (
      <div>
        <Dimmer
          inverted style={{ zIndex: 20 }}
          active={this.state.loading && this.state.data.length > 0}
        >
          <Loader inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
        </Dimmer>
        <Table
          striped unstackable
          selectable
        >
          <Table.Header fullWidth>
            <Table.Row>
              <Table.HeaderCell width={9}>{t('Label', { ns: TRANSLATION_FILES.screens })}</Table.HeaderCell>
              <Table.HeaderCell width={3} textAlign="right">{t('Enabled', { ns: TRANSLATION_FILES.screens })}</Table.HeaderCell>
              <Table.HeaderCell width={1} />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {
              this.state.data.length > 0 ?
              this.state.data.map((field, key) => this.renderFinanceItem(key, field)) : (
                <Table.Row>
                  <Table.Cell colSpan={3} textAlign="center">
                    {
                      this.state.loading ?
                        <Loader active inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
                      : <p>{t('No finance sources found.', { ns: TRANSLATION_FILES.translation })}</p>
                    }
                  </Table.Cell>
                </Table.Row>
              )
            }
            <Table.Row>
              <Table.Cell colSpan={3} textAlign="center">
                <Button
                  primary compact
                  disabled={this.state.loading}
                  onClick={this.handleOnAddFinanceSource}
                >
                  + {t('Add New Source', { ns: TRANSLATION_FILES.translation })}
                </Button>
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </div>
    );
  }
}

export default withTranslation(namespaces, { withRef: true })(FinanceSourceList);



// WEBPACK FOOTER //
// ./src/components/FinanceSourceList.jsx